/* eslint-disable max-len */
import { useRouter } from 'next/router';
import {
	FunctionComponent, useCallback, useEffect, useMemo, useRef,
} from 'react';
import styled from 'styled-components';

import { useCapsulesFromRouteSlug } from '../../../data/Capsule.swr';
import { MapStore } from '../../../data/MapStore';
import { Capsule } from '../../../models/Capsule';
import { DirectionProfiles } from '../../../models/MapBox';
import { DistanceIconValue } from '../DistanceIconValue';
import { SwipeMovement } from '../SwipeMovement';

import { ParcoursCapsuleItem } from './ParcoursCapsuleItem';
import { DurationIconValue } from '../DurationIconValue';

interface Props {
	routeSlug: string;

	timeWalking?: number;
	timeCycling?: number;
	timeDriving?: number;

	directionProfile: DirectionProfiles;
	onDirectionProfile: (directionProfile: DirectionProfiles) => void;
}

const StyledParcoursInfos = styled.div`
	background-color: ${(props) => props.theme.white};
	box-shadow: 0px -2px 8px rgba(0, 0, 0, 0.05);
	border-radius: 16px 16px 0 0;
	width: 100%;
	padding: ${(props) => 6 / props.theme.fontSizeBase}rem 0 0 0;
	position: absolute;
	bottom: 0;
	z-index: 20;
`;

const StyledDistances = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-evenly;
	// gap: ${(props) => 8 / props.theme.fontSizeBase}rem;
	// padding: ${(props) => 14 / props.theme.fontSizeBase}rem ${(props) => 20 / props.theme.fontSizeBase}rem ${(props) => 22 / props.theme.fontSizeBase}rem ${(props) => 20 / props.theme.fontSizeBase}rem;
	padding: ${(props) => 20 / props.theme.fontSizeBase}rem 0;

	@media only screen and (max-width : 375px) {
		// gap: ${(props) => 5 / props.theme.fontSizeBase}rem;
		padding: ${(props) => 7 / props.theme.fontSizeBase}rem 0;
	}
`;

const StyledIndicator = styled.div`
	width: ${(props) => 48 / props.theme.fontSizeBase}rem;
	height: ${(props) => 2 / props.theme.fontSizeBase}rem;
	background: rgba(166, 166, 166, 0.4);
	margin: 0 auto;
	border-radius: 100px;
`;

const StyledListItemContainer = styled.div`
	overflow-y: scroll;
	padding-top: ${(props) => 10 / props.theme.fontSizeBase}rem;

	max-height: 288px;
	@media only screen and (max-width : 321px) {
		max-height: 196px;
	}

	::-webkit-scrollbar {
		-webkit-appearance: none;
	}

	::-webkit-scrollbar:vertical {
		width: 8px;
	}

	::-webkit-scrollbar-thumb {
		background-color: rgba(0, 0, 0, .5);
		border-radius: 10px;
		border: 2px solid #ffffff;
	}

	::-webkit-scrollbar-track {
		border-radius: 10px;
		background-color: #ffffff;
	}
`;

const StyledDurationContainer = styled.div`
	display: flex;
	align-items: center;
`;

export const MapParcoursInfos: FunctionComponent<Props> = ({
	routeSlug, timeWalking, timeCycling, timeDriving, directionProfile, onDirectionProfile,
}: Props) => {
	const router = useRouter();
	const ref = useRef<HTMLDivElement>(null);

	const {
		mapWalk, mapBike, mapCar,
	} = MapStore.useState((s) => s);

	const { data: capsules } = useCapsulesFromRouteSlug(routeSlug, router.locale || 'fr');

	const setOffsetHeight = useCallback(() => {
		MapStore.update((s) => {
			if (ref.current && ref.current.clientHeight > 0) {
				s.heightOffsetButtons = ref.current.clientHeight + 9;
			}
		});
	}, []);

	useEffect(() => {
		if (capsules) {
			setOffsetHeight();
		}

		return () => {
			MapStore.update((s) => {
				s.heightOffsetButtons = 0;
			});
		};
	}, [capsules, setOffsetHeight]);

	const onCapsuleClick = useCallback((capsule: Capsule) => {
		MapStore.update((s) => {
			s.capsuleSelected = capsule;
		});

		router.push({
			pathname: `/map/parcours/${routeSlug}/capsule/${capsule.attributes.slug}`,
			query:    {
				directionProfile,
			},
		});
	}, [directionProfile, routeSlug, router]);

	const totalDuration = useMemo(() => {
		let duration = 0;
		capsules?.forEach((c) => {
			duration += c.attributes.duration;
		});
		return duration;
	}, [capsules]);

	return (
		<SwipeMovement>
			<StyledParcoursInfos ref={ref}>
				<StyledIndicator/>

				<StyledDistances>
					{ mapWalk && <DistanceIconValue onClick={onDirectionProfile} selected={directionProfile === 'walking'} value={timeWalking || 0} directionProfile='walking' /> }
					{ mapBike && <DistanceIconValue onClick={onDirectionProfile} selected={directionProfile === 'cycling'} value={timeCycling || 0} directionProfile='cycling' /> }
					{ mapCar && <DistanceIconValue onClick={onDirectionProfile} selected={directionProfile === 'driving'} value={timeDriving || 0} directionProfile='driving' /> }

					<StyledDurationContainer>
						<DurationIconValue value={totalDuration}/>
					</StyledDurationContainer>
				</StyledDistances>

				<StyledListItemContainer>
					{ capsules && capsules.map((capsule) => (
						<ParcoursCapsuleItem
							key={capsule.attributes.slug}
							onClick={onCapsuleClick}
							capsule={capsule}
							directionProfile={directionProfile}
						/>
					))}
				</StyledListItemContainer>
			</StyledParcoursInfos>
		</SwipeMovement>
	);
};
